import React, { useEffect, useRef, useState } from "react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { toast } from "components/ui/toast";
import TextEditor from "components/templates/template-editor/text-editor/Editor";
import {
    getFooterPresignedUrl,
    getFooterTemplate,
    updateUserProfileRes,
} from "core";
import { PageContainer } from "../ui/page-container";
import ProfileHeader from "./ProfileHeader";
import {
    User,
    Mail,
    Phone,
    Building2,
    Lock,
    Save,
    Eye,
    Code2,
    AlertCircle,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import AccountDeletion from "../account/AccountDeletion";

/* ─────────────────────────────────────────────
   Tiny helpers
───────────────────────────────────────────── */

function FieldError({ msg }: { msg: string }) {
    return (
        <p className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400 mt-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            {msg}
        </p>
    );
}

interface FieldProps {
    id: string;
    label: string;
    required?: boolean;
    icon: React.ReactNode;
    error?: string;
    children: React.ReactNode;
    hint?: string;
}
function Field({ id, label, required, icon, error, children, hint }: FieldProps) {
    return (
        <div className="space-y-1.5">
            <label
                htmlFor={id}
                className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700 dark:text-gray-300"
            >
                <span className="text-content-subtle dark:text-gray-500">{icon}</span>
                {label}
                {required && <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>}
            </label>
            {children}
            {error ? <FieldError msg={error} /> : hint ? (
                <p className="text-[11px] text-content-subtle dark:text-gray-500 mt-1">{hint}</p>
            ) : null}
        </div>
    );
}

function SectionCard({
    accentFrom,
    accentTo,
    children,
}: {
    accentFrom: string;
    accentTo: string;
    children: React.ReactNode;
}) {
    return (
        <div className="relative bg-surface dark:bg-gray-900 rounded-2xl border border-line dark:border-gray-800 shadow-sm overflow-hidden">
            {/* 3-px gradient top accent */}
            <div className={`absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r ${accentFrom} ${accentTo}`} />
            <div className="pt-6 px-6 pb-6">{children}</div>
        </div>
    );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <h2 className="text-base font-bold text-content dark:text-white tracking-tight leading-tight">
            {children}
        </h2>
    );
}
function SectionSubtitle({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-[13px] text-content-muted dark:text-gray-400 mt-0.5">
            {children}
        </p>
    );
}

export default function UserProfile() {

    const [form, setForm] = useState({ email: "", name: "", phone: "", organization: "" });
    const savedForm = useRef({ email: "", name: "", phone: "", organization: "" });
    const isDirty =
        form.name !== savedForm.current.name ||
        form.phone !== savedForm.current.phone ||
        form.organization !== savedForm.current.organization;

    const [formError, setFormError] = useState({ email: "", name: "", phone: "", organization: "" });
    const [touched, setTouched] = useState({ email: false, name: false, phone: false, organization: false });

    const [content, setContent] = useState("");
    const [editorTab, setEditorTab] = useState<"edit" | "preview">("edit");
    const customEditorRef = useRef<any>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [saving, setSaving] = useState(false);
    const [footerSaving, setFooterSaving] = useState(false);

    const computeError = (field: keyof typeof form, value: string): string => {
        const v = (value ?? "").trim();
        switch (field) {
            case "email": {
                if (!v) return "";
                return /^[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(v) ? "" : "Please enter a valid email";
            }
            case "phone": {
                if (!v) return "Please provide a valid phone number";
                return /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(v)
                    ? "" : "Please provide a valid phone number";
            }
            case "name": {
                if (!v) return "Please provide a valid name";
                return v.length >= 2 ? "" : "Please provide a valid name";
            }
            case "organization": {
                if (!v) return "Please provide a valid organisation";
                return v.length >= 2 ? "" : "Please provide a valid organisation";
            }
            default: return "";
        }
    };

    const touchField = (field: keyof typeof form) =>
        setTouched(prev => ({ ...prev, [field]: true }));

    const validateField = (field: keyof typeof form) => {
        const err = computeError(field, form[field]);
        setFormError(prev => ({ ...prev, [field]: err }));
        return !err;
    };

    const validateAll = () => {
        setTouched({ email: true, name: true, phone: true, organization: true });
        const next = {
            email: computeError("email", form.email),
            name: computeError("name", form.name),
            phone: computeError("phone", form.phone),
            organization: computeError("organization", form.organization),
        };
        setFormError(next);
        return Object.values(next).every(e => e === "");
    };

    const handleChange = (field: keyof typeof form, value: string) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (touched[field]) {
            const err = computeError(field, value);
            setFormError(prev => ({ ...prev, [field]: err }));
        }
    };

    const updateUserProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateAll()) return;
        setSaving(true);
        try {
            const res = await updateUserProfileRes({
                name: form.name || undefined,
                phoneNumber: form.phone || undefined,
                organization: form.organization || undefined,
            });
            toast.success(typeof res?.data === "string" ? res.data : "Profile updated successfully!");
            savedForm.current = { ...form };
        } catch (err: any) {
            toast.error(typeof err?.response?.data === "string" ? err.response.data : "Error updating user profile");
        } finally {
            setSaving(false);
        }
    };

    const getPreAssignedFooterUrl = async () => (await getFooterPresignedUrl()).data;

    const uploadFooterDetails = async (objectFields: any, file: Blob, url: string) => {
        const fd = new FormData();
        fd.append("key", objectFields["key"]);
        fd.append("AWSAccessKeyId", objectFields["AWSAccessKeyId"]);
        fd.append("x-amz-security-token", objectFields["x-amz-security-token"]);
        fd.append("policy", objectFields["policy"]);
        fd.append("signature", objectFields["signature"]);
        fd.append("file", file, "footer.html");
        console.log("Uploading footer to:", url);
        return (await fetch(url, { method: "POST", body: fd })).status;
    };

    const handleFooterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!content.trim()) { toast.error("Footer content cannot be empty!"); return; }
        setFooterSaving(true);
        try {
            toast.info("Updating footer...");
            const preData = await getPreAssignedFooterUrl();
            const fileBlob = new Blob([content], { type: "text/html" });
            const status = await uploadFooterDetails(preData.objectFields, fileBlob, preData.objectUrl);
            if (status === 200 || status === 204) {
                toast.success("Footer Updated Successfully!");
                const footerRes = await getFooterTemplate(preData.fileObject);
                setContent(footerRes.data ?? "");
            } else {
                toast.error("Unexpected response from server");
            }
        } catch (err) {
            console.error("Footer upload error:", err);
            toast.error("Failed to upload footer!");
        } finally {
            toast.dismiss();
            setFooterSaving(false);
        }
    };

    useEffect(() => {
        const loadProfile = async () => {
            setLoadingProfile(true);
            try {
                const preContent = await getPreAssignedFooterUrl();
                const { profileDetails, fileObject } = preContent;
                const loaded = {
                    email: profileDetails.email ?? "",
                    name: profileDetails.name ?? "",
                    phone: profileDetails.phone_number ?? "",
                    organization: profileDetails.organization ?? "",
                };
                savedForm.current = loaded;
                setForm(loaded);
                const footerRes = await getFooterTemplate(fileObject);
                setContent(footerRes.data ?? "");
            } catch {
                toast.error("Failed to load profile!");
            } finally {
                setLoadingProfile(false);
            }
        };
        loadProfile();
    }, []);

    return (
        <PageContainer>
            <ProfileHeader />
            <div className="space-y-6 mx-auto">
                <SectionCard accentFrom="from-emerald-400" accentTo="to-teal-500">
                    <div className="flex items-start justify-between gap-4 mb-5">
                        <div>
                            <SectionTitle>Profile Information</SectionTitle>
                            <SectionSubtitle>Update your name, phone, and organisation.</SectionSubtitle>
                        </div>
                    </div>

                    {isDirty && (
                        <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg
                            bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                            <span className="text-[12px] font-medium text-amber-700 dark:text-amber-400">
                                You have unsaved changes
                            </span>
                        </div>
                    )}

                    <form onSubmit={updateUserProfile} noValidate>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                            {/* Name */}
                            <Field
                                id="profile-name"
                                label="Full Name"
                                required
                                icon={<User className="w-3.5 h-3.5" />}
                                error={touched.name ? formError.name : ""}
                            >
                                <Input
                                    id="profile-name"
                                    value={form.name}
                                    onChange={e => handleChange("name", e.target.value)}
                                    onBlur={() => { touchField("name"); validateField("name"); }}
                                    placeholder="Enter your name"
                                    aria-invalid={!!(touched.name && formError.name)}
                                    disabled={loadingProfile}
                                />
                            </Field>

                            {/* Email — locked */}
                            <Field
                                id="profile-email"
                                label="Email Address"
                                icon={<Mail className="w-3.5 h-3.5" />}
                                hint="Email cannot be changed"
                            >
                                <Input
                                    id="profile-email"
                                    value={form.email}
                                    disabled
                                    trailing={<Lock className="w-3.5 h-3.5 text-content-subtle" />}
                                    className="opacity-60 cursor-not-allowed"
                                />
                            </Field>

                            {/* Phone */}
                            <Field
                                id="profile-phone"
                                label="Phone Number"
                                required
                                icon={<Phone className="w-3.5 h-3.5" />}
                                error={touched.phone ? formError.phone : ""}
                            >
                                <Input
                                    id="profile-phone"
                                    value={form.phone}
                                    onChange={e => handleChange("phone", e.target.value)}
                                    onBlur={() => { touchField("phone"); validateField("phone"); }}
                                    placeholder="Enter your phone number"
                                    aria-invalid={!!(touched.phone && formError.phone)}
                                    disabled={loadingProfile}
                                />
                            </Field>

                            {/* Organisation */}
                            <Field
                                id="profile-org"
                                label="Organisation"
                                required
                                icon={<Building2 className="w-3.5 h-3.5" />}
                                error={touched.organization ? formError.organization : ""}
                            >
                                <Input
                                    id="profile-org"
                                    value={form.organization}
                                    onChange={e => handleChange("organization", e.target.value)}
                                    onBlur={() => { touchField("organization"); validateField("organization"); }}
                                    placeholder="Enter your organisation"
                                    aria-invalid={!!(touched.organization && formError.organization)}
                                    disabled={loadingProfile}
                                />
                            </Field>
                        </div>

                        {/* Submit row */}
                        <div className="mt-6 flex items-center justify-between pt-5 border-t border-line-subtle dark:border-gray-800">
                            <div className="flex items-center gap-2 text-[12px] text-content-subtle dark:text-gray-500">
                                {!isDirty && !saving && (
                                    <>
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                                        All changes saved
                                    </>
                                )}
                            </div>
                            <Button
                                type="submit"
                                disabled={!isDirty || saving || loadingProfile}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                                    bg-gradient-to-r from-emerald-500 to-teal-600
                                    hover:from-emerald-600 hover:to-teal-700
                                    text-white text-[13px] font-semibold shadow-sm
                                    hover:shadow-md hover:shadow-emerald-500/20
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-all duration-200"
                            >
                                {saving
                                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</>
                                    : <><Save className="w-3.5 h-3.5" />Save Changes</>
                                }
                            </Button>
                        </div>
                    </form>
                </SectionCard>

                <SectionCard accentFrom="from-violet-500" accentTo="to-indigo-600">
                    {/* Card header with tab switcher on the right */}
                    <div className="flex items-center justify-between gap-4 mb-5">
                        <div>
                            <SectionTitle>Email Footer Template</SectionTitle>
                            <SectionSubtitle>Customise the HTML footer appended to all your outgoing emails.</SectionSubtitle>
                        </div>
                        {/* Tab switcher — right side of header */}
                        <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                            {(["edit", "preview"] as const).map(tab => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setEditorTab(tab)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold
                                        transition-all duration-150
                                        ${editorTab === tab
                                            ? "bg-surface dark:bg-gray-900 text-content dark:text-white shadow-sm"
                                            : "text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                        }`}
                                >
                                    {tab === "edit"
                                        ? <><Code2 className="w-3.5 h-3.5" />Editor</>
                                        : <><Eye className="w-3.5 h-3.5" />Preview</>
                                    }
                                </button>
                            ))}
                        </div>
                    </div>

                    <form onSubmit={handleFooterSubmit} noValidate>
                        {/* Editor / Preview pane */}
                        <div className="rounded-xl border border-line dark:border-gray-700 overflow-hidden shadow-sm" style={{ minHeight: 420 }}>
                            {editorTab === "edit" ? (
                                <TextEditor
                                    ref={customEditorRef}
                                    content={content}
                                    setContent={setContent}
                                />
                            ) : (
                                <div className="p-6 bg-surface dark:bg-gray-950" style={{ minHeight: 420 }}>
                                    {content.trim() ? (
                                        <>
                                            <p className="text-[11px] font-bold uppercase tracking-widest text-content-subtle dark:text-gray-500 mb-4">
                                                Live Preview
                                            </p>
                                            <div
                                                className="prose prose-sm dark:prose-invert max-w-none"
                                                dangerouslySetInnerHTML={{ __html: content }}
                                            />
                                        </>
                                    ) : (
                                        <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center">
                                            <Eye className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-3" />
                                            <p className="text-sm font-medium text-content-subtle dark:text-gray-500">
                                                Nothing to preview yet
                                            </p>
                                            <p className="text-[12px] text-content-subtle dark:text-gray-600 mt-1">
                                                Switch to the Editor tab and add some content.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex items-center justify-end pt-4 border-t border-line-subtle dark:border-gray-800">
                            <Button
                                type="submit"
                                disabled={footerSaving || loadingProfile}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                                    bg-gradient-to-r from-violet-500 to-indigo-600
                                    hover:from-violet-600 hover:to-indigo-700
                                    text-white text-[13px] font-semibold shadow-sm
                                    hover:shadow-md hover:shadow-violet-500/20
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-all duration-200"
                            >
                                {footerSaving
                                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Updating…</>
                                    : <><Save className="w-3.5 h-3.5" />Update Footer</>
                                }
                            </Button>
                        </div>
                    </form>
                </SectionCard>

                <AccountDeletion />

            </div>
        </PageContainer>
    );
}
